import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { Typography, Box, Divider } from "@mui/material";
import { CalendarToday as CalendarIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import LoadingBackdrop from "../components/common/custom-component/LoadingBackdrop";
import DataContainer, { TableActionButton, TableActionGroup } from "../components/common/DataContainer";
import { useSearchFilter } from "../hooks/useSearchFilter";
import { useAuth } from "../hooks/useAuth";
import { useToast } from "../components/common/custom-component/ToastProvider";
import EnhancedConfirmationDialog from "../components/common/custom-component/EnhancedConfirmationDialog";
import SearchFilterBar from "../components/common/SearchFilterBar";
import CustomPagination from "../components/common/CustomPagination";
import { useTableColumns } from "../hooks/useTableColumns";
import { logger } from "helpers/logger";

import { getCompanyContactsThunk, addUserThunk, patchDatabaseRecord, fetchCompanyRolesThunk, postDatabaseRecordThunk, deleteDatabaseRecordThunk } from "../store/thunks/mdsThunks";
import { createAddUserRequest } from "../dto/userDto";
import { manageCompanyContactsReducer, initialState } from "../reducers/manageCompanyContactsReducer";
import { id_number } from "common/ErrorMessages";

export default function ManageCompanyContactsPage() {
	const keycloakAuth = useAuth();
	const dispatch = useDispatch();
	const [t] = useTranslation();

	const { success, error: ToastError } = useToast();
	const selectedCompany = useSelector((state) => state.selectedCompany.data?.companyData);
	const companyRoles = useSelector((state) => state.selectedCompany.data?.companyRoles);

	const [contactsData, setContactsData] = useState([]);
	const [deletedRecordIds, setDeletedRecordIds] = useState(new Set());

	// Reducer for dialog and form state management
	const [state, dispatchState] = useReducer((state, action) => manageCompanyContactsReducer(state, action), initialState);

	// Ref to track ongoing thunk requests
	const activeThunksRef = useRef([]);

	const fetchContacts = useCallback(async () => {
		if (!selectedCompany?.companyId) return;

		dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

		try {
			const thunkPromise = dispatch((getCompanyContactsThunk)({ companyId: selectedCompany.companyId }));
			activeThunksRef.current.push(thunkPromise);

			const result = await thunkPromise;
			activeThunksRef.current = activeThunksRef.current.filter((t) => t !== thunkPromise);

			if (getCompanyContactsThunk.fulfilled.match(result)) {
				const records = result.payload?.data?.records || result.payload || [];
				setContactsData(Array.isArray(records) ? records : []);
			} else if (getCompanyContactsThunk.rejected.match(result)) {
				logger.error("Failed to fetch company contacts:", result.payload);
				ToastError(result.payload || "Failed to fetch company contacts", { severity: "error" });
			}
		} catch (err) {
			logger.error("Unexpected error in fetchContacts:", err);
			ToastError("An unexpected error occurred while fetching contacts", { severity: "error" });
		} finally {
			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
		}
	}, [dispatch, selectedCompany?.companyId, ToastError]);

	useEffect(() => {
		fetchContacts();
	}, [fetchContacts]);

	// Cleanup function to abort all active thunks on unmount
	useEffect(() => {
		return () => {
			activeThunksRef.current.forEach((thunk) => {
				if (thunk && typeof thunk.abort === "function") {
					thunk.abort();
				}
			});
			activeThunksRef.current = [];
		};
	}, []);

	// Format display data for table
	const displayData = useMemo(() => {
		const rawList = Array.isArray(contactsData) ? contactsData : [];
		return rawList
			.filter((record) => !deletedRecordIds.has(record.id || record.userId))
			.map((record) => {
				const firstName = record.firstName || record.first_name || "";
				const lastName = record.lastName || record.last_name || "";
				const fullName = `${firstName} ${lastName}`.trim() || record.name || "";
				return {
					...record,
					firstName,
					lastName,
					fullName,
					email: record.email || "",
					mobileNumber: record.mobile_number || record.mobileNumber || "",
					idType: record.id_type || record.idType || "",
					idNumber: record.id_number || record.idNumber || "",
					userId: record.userId || record.user_id || null,
					createdDate: record.created_at || record.createdDate || "",
				};
			});
	}, [contactsData, deletedRecordIds]);

	// #region SEARCH & PAGINATION
	const { setSearchQuery, filteredData } = useSearchFilter(
		displayData,
		["fullName", "firstName", "lastName", "email", "mobileNumber", "idType", "idNumber", "createdDate"],
		null
	);

	const totalPages = Math.max(1, Math.ceil((filteredData?.length || 0) / state.table.recordsPerPage));

	const pagedData = useMemo(() => {
		const start = (state.table.currentPage - 1) * state.table.recordsPerPage;
		const end = start + state.table.recordsPerPage;
		return (filteredData || []).slice(start, end);
	}, [filteredData, state.table.currentPage, state.table.recordsPerPage]);
	// #endregion

	// #region ADD PROCESS
	const handleAddClick = () => {
		dispatchState({ type: "OPEN_ADD_DIALOG" });
	};

	const handleConfirmAdd = useCallback(async (formData) => {
		dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });
		try {
			logger.info("Add Contact Form Data:", formData);

			const jsonBody = {
				tableName: "tbl_contacts",
				records: [
					{
						companyId: selectedCompany?.companyId,
						firstName: formData.firstName,
						lastName: formData.lastName,
						email: formData.email,
						mobile_number: formData.mobileNumber,
						id_type: formData.idType,
						id_number: formData.idNumber,
					},
				],
			};

			const result = await dispatch(postDatabaseRecordThunk({ jsonBody }));

			if (postDatabaseRecordThunk.fulfilled.match(result)) {
				success("Contact added successfully");
				dispatchState({ type: "CLOSE_ADD_DIALOG" });
				fetchContacts();
			} else {
				ToastError(result.payload || "Failed to add contact", { severity: "error" });
			}
		} catch (error) {
			logger.error("Error in handleConfirmAdd:", error);
			ToastError("An unexpected error occurred", { severity: "error" });
		} finally {
			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
		}
	}, [dispatch, selectedCompany, success, ToastError, fetchContacts]);

	const handleCancelAdd = useCallback(() => {
		dispatchState({ type: "CLOSE_ADD_DIALOG" });
	}, []);
	// #endregion

	// #region EDIT PROCESS
	const handleEditClick = useCallback((row) => {
		const editFormData = {
			firstName: row.firstName || "",
			lastName: row.lastName || "",
			email: row.email || "",
			mobile_number: row.mobile_number || row.mobileNumber || "",
			id_type: row.id_type || row.idType || "",
			id_number: row.id_number || row.idNumber || "",
		};
		dispatchState({
			type: "OPEN_EDIT_DIALOG",
			payload: {
				record: row,
				editFormData,
			},
		});
	}, []);

	const handleCancelEdit = useCallback(() => {
		dispatchState({ type: "CLOSE_EDIT_DIALOG" });
	}, []);

	const handleConfirmEdit = useCallback(
		async (changedFields, fullData) => {
			if (!state.dialogs.selectedRecord?.id) {
				dispatchState({ type: "CLOSE_EDIT_DIALOG" });
				return;
			}

			logger.info("Changed fields:", changedFields);
			if (!changedFields || Object.keys(changedFields).length === 0) {
				dispatchState({ type: "CLOSE_EDIT_DIALOG" });
				return;
			}

			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

			try {
				const jsonToPatch = {
					tableName: "tbl_contacts",
					fields: changedFields,
					matchField: "id",
					matchValue: state.dialogs.selectedRecord.id,
				};

				const result = await dispatch(patchDatabaseRecord({ JsonToPatch: jsonToPatch }));

				if (patchDatabaseRecord.fulfilled.match(result)) {
					success("Contact updated successfully");
					dispatchState({ type: "CLOSE_EDIT_DIALOG" });
					fetchContacts();
				} else {
					ToastError(result.payload || "Failed to update contact", { severity: "error" });
				}
			} catch (error) {
				logger.error("Error in handleConfirmEdit:", error);
				ToastError("An unexpected error occurred", { severity: "error" });
			} finally {
				dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
			}
		},
		[dispatch, state.dialogs.selectedRecord, success, ToastError, fetchContacts]
	);
	// #endregion

	// #region DELETE PROCESS
	const handleDeleteClick = useCallback((row) => {
		dispatchState({
			type: "OPEN_DELETE_DIALOG",
			payload: { ...row },
		});
	}, []);

	const handleCancelDelete = useCallback(() => {
		dispatchState({ type: "CLOSE_DELETE_DIALOG" });
	}, []);

	const handleConfirmDelete = useCallback(async () => {
		if (!state.dialogs.selectedRecord?.id) {
			dispatchState({ type: "CLOSE_DELETE_DIALOG" });
			return;
		}

		dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

		try {
			const recordToDelete = state.dialogs.selectedRecord;
			const idToDelete = recordToDelete.id || recordToDelete.userId;

			const jsonBody = {
				tableName: "tbl_contacts",
				matchField: "id",
				matchValue: recordToDelete.id,
			};

			const result = await dispatch(deleteDatabaseRecordThunk({ jsonBody }));

			if (deleteDatabaseRecordThunk.fulfilled.match(result)) {
				setDeletedRecordIds((prev) => new Set(prev).add(idToDelete));
				success("Contact deleted successfully");
				dispatchState({ type: "CLOSE_DELETE_DIALOG" });
				fetchContacts();
			} else {
				ToastError(result.payload || "Failed to delete contact", { severity: "error" });
			}
		} catch (error) {
			logger.error("Error in handleConfirmDelete:", error);
			ToastError("An unexpected error occurred", { severity: "error" });
		} finally {
			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
		}
	}, [dispatch, state.dialogs.selectedRecord, success, ToastError, fetchContacts]);
	// #endregion

	// #region MAKE LEGIT PROCESS
	const handleMakeLegit = useCallback(
		(row) => {
			dispatchState({
				type: "OPEN_MAKE_LEGIT_DIALOG",
				payload: { record: row, roleOptions: companyRoles || [] },
			});
		},
		[companyRoles]
	);

	const handleCancelMakeLegit = useCallback(() => {
		dispatchState({ type: "CLOSE_MAKE_LEGIT_DIALOG" });
	}, []);

	const handleConfirmMakeLegit = useCallback(
		async (formData) => {
			if (!state.dialogs.selectedRecord) return;
			const record = state.dialogs.selectedRecord;

			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

			try {
				const body = createAddUserRequest({
					firstName: record.firstName,
					lastName: record.lastName,
					email: record.email,
					mobileNumber: record.mobileNumber,
					IdType: record.idType,
					idNumber: record.idNumber,
					Roles: formData.Roles,
				});

				const result = await dispatch(
					addUserThunk({
						companyId: selectedCompany.companyId,
						keycloakCompanyId: selectedCompany.keycloakCompanyId,
						body: body,
					})
				);

				if (addUserThunk.fulfilled.match(result)) {
					const newUserId = result.payload?.data?.message;

					if (newUserId) {
						// Patch supabase
						const patchResult = await dispatch(
							patchDatabaseRecord({
								JsonToPatch: {
									tableName: "tbl_contacts",
									fields: {
										userId: newUserId,
									},
									matchField: "id",
									matchValue: record.id,
								},
							})
						);

						if (patchDatabaseRecord.fulfilled.match(patchResult)) {
							success("Contact successfully made legit");
							fetchContacts(); // Refresh list
						} else {
							ToastError("User created, but failed to link contact record.", { severity: "error" });
						}
					} else {
						success("User created successfully, but backend didn't return a userId to link.");
					}
					dispatchState({ type: "CLOSE_MAKE_LEGIT_DIALOG" });
				} else if (addUserThunk.rejected.match(result)) {
					logger.error("Failed to make legit (add user):", result.payload);
					ToastError(result.payload || "Failed to make contact legit.", { severity: "error" });
				}
			} catch (err) {
				logger.error("Unexpected error in handleConfirmMakeLegit:", err);
				ToastError("An unexpected error occurred.", { severity: "error" });
			} finally {
				dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
			}
		},
		[dispatch, selectedCompany, state.dialogs.selectedRecord, success, ToastError, fetchContacts]
	);
	// #endregion

	// #region TABLE COLUMNS
	const actions = useMemo(
		() => ({
			onDelete: handleDeleteClick,
			onEdit: handleEditClick,
			onLegit: handleMakeLegit,
		}),
		[handleDeleteClick, handleEditClick, handleMakeLegit]
	);

	const { columns } = (useTableColumns)("ManageCompanyContacts", { actions, context: null, fallbackColumns: [], skipFetch: false });
	// #endregion

	return (
		<Box sx={{ p: 3 }}>
			{/* Header */}
			<Typography variant="h3" gutterBottom color="primary">
				Manage Company Contacts Page
			</Typography>

			<Typography variant="body1" color="text.secondary" paragraph>
				This is a Manage Company Contacts page.
			</Typography>

			<Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, px: 2 }}>
				<TableActionButton text="Add New Contact" variant="contained" onClick={handleAddClick} />
			</Box>

			{!contactsData || contactsData.length === 0 ? (
				<DataContainer
					data={[]}
					columns={columns}
					emptyStateIcon={CalendarIcon}
					emptyStateTitle="No contacts in this company"
					emptyStateSubtitle="Start adding contacts to your company"
				/>
			) : (
				<>
					<Box sx={{ p: 2 }}>
						<Box
							sx={{
								my: 2,
								display: "flex",
								flexDirection: "column",
								gap: 2,
							}}
						>
							<TableActionGroup sx={{ display: "flex", justifyContent: "flex-end" }}>{null}</TableActionGroup>
							<SearchFilterBar
								mode="frontend"
								searchValue={state.table.searchInput}
								onSearchChange={(v) => {
									dispatchState({ type: "SET_SEARCH_INPUT", payload: v });
									setSearchQuery(v);
								}}
								showFilterButton={false}
								showSearchButton={false}
								searchPlaceholder={t("Search contacts...")}
							/>
						</Box>

						<DataContainer data={pagedData} columns={columns} showRowNumbers maxHeight={600} stickyHeader />
					</Box>

					<Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
						<CustomPagination
							currentPage={state.table.currentPage}
							totalPages={totalPages}
							onPageChange={(e, v) => dispatchState({ type: "SET_CURRENT_PAGE", payload: v })}
							showFirstButton
							showLastButton
						/>
					</Box>
				</>
			)}

			<EnhancedConfirmationDialog
				open={state.dialogs.deleteConfirm}
				onClose={handleCancelDelete}
				onConfirm={handleConfirmDelete}
				title="Confirm Delete"
				message="Are you sure you want to delete this contact? This action cannot be undone."
				confirmText="DELETE"
				confirmColor="error"
				cancelText="Cancel"
			/>

			<EnhancedConfirmationDialog
				open={state.dialogs.editConfirm}
				onClose={handleCancelEdit}
				onConfirm={handleConfirmEdit}
				title="Confirm Edit"
				confirmText="Edit"
				cancelText="Cancel"
				showMessage={false}
				returnChangedFieldsOnly={true}
				defaultValues={state.forms.editContactForm}
				fields={[
					{
						name: "firstName",
						label: "First Name",
						type: "text",
						required: true,
						placeholder: "Enter first name",
					},
					{
						name: "lastName",
						label: "Last Name",
						type: "text",
						required: true,
						placeholder: "Enter last name",
					},
					{
						name: "email",
						label: "Email",
						type: "text",
						regex: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
						regexErrorMessage: "Please enter a valid email address",
						placeholder: "Enter email address",
					},
					{
						name: "mobile_number",
						label: "Mobile Number",
						type: "text",
						required: true,
						placeholder: "Enter mobile number (e.g., 0512345678)",
						regex: /^05\d{8}$/,
						regexErrorMessage: "Mobile number must start with 05 and be exactly 10 digits",
						inputMode: "numeric",
						pattern: "[0-9]*",
						numericOnly: true,
					},
					{
						name: "id_type",
						label: "Select ID Type",
						type: "text",
						disabled: true,
					},
					{
						name: "id_number",
						label: "ID Number",
						type: "text",
						disabled: true,
					},
				]}
			/>

			<EnhancedConfirmationDialog
				open={state.dialogs.addConfirm}
				onClose={handleCancelAdd}
				onConfirm={handleConfirmAdd}
				title="Add New Contact"
				message="Add contact to your company"
				cancelText="Cancel"
				confirmText="Confirm"
				confirmColor="primary"
				showMessage={false}
				defaultValues={state.forms.addContactForm}
				fields={[
					{
						name: "firstName",
						label: "First Name",
						type: "text",
						required: true,
						placeholder: "Enter first name",
					},
					{
						name: "lastName",
						label: "Last Name",
						type: "text",
						required: true,
						placeholder: "Enter last name",
					},
					{
						name: "email",
						label: "Email",
						type: "text",
						regex: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
						regexErrorMessage: "Please enter a valid email address",
						placeholder: "Enter email address",
					},
					{
						name: "mobileNumber",
						label: "Mobile Number",
						type: "text",
						required: true,
						placeholder: "Enter mobile number (e.g., 0512345678)",
						regex: /^05\d{8}$/,
						regexErrorMessage: "Mobile number must start with 05 and be exactly 10 digits",
						inputMode: "numeric",
						pattern: "[0-9]*",
						numericOnly: true,
					},
					{
						name: "idType",
						label: "Select ID Type",
						type: "select",
						required: true,
						options: [
							{ label: "Iqama", value: "Iqama" },
							{ label: "National ID", value: "National ID" },
						],
					},
					{
						name: "idNumber",
						label: "ID Number",
						type: "text",
						required: true,
						placeholder: "Enter ID Number (10 digits)",
						regex: /^\d{10}$/,
						regexErrorMessage: "ID Number must be exactly 10 digits",
						inputMode: "numeric",
						pattern: "[0-9]*",
						numericOnly: true,
					},
				]}
			/>

			<EnhancedConfirmationDialog
				open={state.dialogs.makeLegitConfirm}
				onClose={handleCancelMakeLegit}
				onConfirm={handleConfirmMakeLegit}
				title="Make Contact Legit"
				message="Assign roles to make this contact a full user."
				cancelText="Cancel"
				confirmText="Confirm"
				confirmColor="primary"
				showMessage={true}
				defaultValues={state.forms.makeLegitFormData}
				fields={[
					{
						name: "Roles",
						label: "Choose user's roles",
						type: "dropdown",
						required: true,
						multiple: true,
						errorMessage: "Please select at least one role",
						options: state.ui.roleOptions,
						placeholder: "Select roles...",
					},
				]}
			/>

			{/* @ts-ignore */}
			<LoadingBackdrop open={state.loading.isLoadingBackdrop} message="Loading data, please wait..." variant="bars" color="greenGradient" />
		</Box>
	);
}
